import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Badge, Button, Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faQrcode,
  faUniversity,
} from "@fortawesome/free-solid-svg-icons";
import {
  DropdownFilter,
  FloatInput,
  FloatNumericInput,
  FloatSelect,
  showToast,
  WizardFooter,
} from "src/componentes";
import { ConsultarPessoa } from "src/hook/Consulta/ConsultaPessoa.Module";
import { PessoaDto } from "src/pages/Privada/Entidade/Pessoa/Pessoa.Dto";
import PessoaCadastroView from "src/pages/Privada/Entidade/Pessoa/Cadastro/PessoaCadastro.View";
import { FormatarCNPJ, FormatarCPF } from "src/hook/Functions";
import {
  EFormaPagamentoMDFe,
  ETipoComponentePagamentoMDFe,
  ETipoEmitenteMDFe,
  formaPagamentoMDFeOptions,
  getLocal,
  MDFeInput,
  STORAGEMDFE_KEY,
  tipoComponentePagamentoMDFeOptions,
  updateLocal,
} from "../MDFe.Dto";
import {
  distribuirValorParcelas,
  PagamentoFormType,
  PagamentoSchema,
} from "./MDFe.Cadastro.Dto";
import moment from "moment";
import BancoCompeInputComponent from "./Componentes/BancoCompeInput.Component";

function MDFeCadastroPagamentoView() {
  const navigate = useNavigate();
  const pessoaBuscar = ConsultarPessoa();
  const [cadastroPagamentoIndex, setCadastroPagamentoIndex] = useState<
    number | null
  >(null);

  const mdfe = (getLocal<MDFeInput>(STORAGEMDFE_KEY) ??
    ({
      pagamentos: [],
      tipoEmitente: ETipoEmitenteMDFe.PrestadorServicoTransporte,
      valorTotalCarga: 0,
    } as unknown as MDFeInput)) as MDFeInput;

  const tipoEmit = Number(mdfe?.tipoEmitente ?? 1);
  const isPrestador = tipoEmit === ETipoEmitenteMDFe.PrestadorServicoTransporte;

  const formatarDoc = (val?: string) => {
    if (!val) return "";
    const limpo = val.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    const temLetras = /[A-Z]/.test(limpo);
    if (!temLetras && limpo.length <= 11) return FormatarCPF(limpo);
    return FormatarCNPJ(limpo);
  };

  const defaultValues: PagamentoFormType = {
    pagamentos: [],
  };

  if (mdfe.pagamentos && mdfe.pagamentos.length > 0) {
    defaultValues.pagamentos = mdfe.pagamentos.map((p) => ({
      nomeBeneficiario: p.nomeBeneficiario || "",
      documento: formatarDoc(
        p.documento ||
          p.cnpjBeneficiario ||
          p.cpfBeneficiario ||
          p.cnpjResponsavelPagamento ||
          p.cpfResponsavelPagamento ||
          ""
      ),
      idEstrangeiroBeneficiario: p.idEstrangeiroBeneficiario || "",
      valorTotalContrato: p.valorTotalContrato ?? 0,
      formaPagamento: p.formaPagamento ?? EFormaPagamentoMDFe.AVista,
      documentoResponsavelPagamento: formatarDoc(
        p.documentoResponsavelPagamento ||
          p.cnpjResponsavelPagamento ||
          p.cpfResponsavelPagamento ||
          ""
      ),
      idEstrangeiroResponsavelPagamento:
        p.idEstrangeiroResponsavelPagamento || "",
      cnpjInstituicaoPagamento: p.cnpjInstituicaoPagamento
        ? FormatarCNPJ(p.cnpjInstituicaoPagamento)
        : "",
      chavePixBancario: p.chavePixBancario || "",
      valorAdiantamento: p.valorAdiantamento ?? undefined,
      tipoAntecipacao: p.tipoAntecipacao ?? undefined,
      codigoBanco: p.codigoBanco || "",
      codigoAgencia: p.codigoAgencia || "",
      indicadorPagamentoBancario: p.indicadorPagamentoBancario ?? undefined,
      componentes:
        p.componentes && p.componentes.length > 0
          ? p.componentes.map((c) => ({
              tipoComponente:
                c.tipoComponente ?? ETipoComponentePagamentoMDFe.Outros,
              valorComponente: c.valorComponente ?? 0,
              descricaoOutro: c.descricaoOutro || "",
            }))
          : [
              {
                tipoComponente: ETipoComponentePagamentoMDFe.Outros,
                valorComponente: p.valorTotalContrato ?? 0,
                descricaoOutro: "FRETE",
              },
            ],
      parcelas:
        p.parcelas && p.parcelas.length > 0
          ? p.parcelas.map((par, pIdx) => ({
              numeroParcela: par.numeroParcela ?? pIdx + 1,
              dataVencimento: par.dataVencimento
                ? moment(par.dataVencimento, [
                    "YYYY-MM-DD",
                    "DD/MM/YYYY",
                    moment.ISO_8601,
                  ]).format("YYYY-MM-DD")
                : "",
              valorParcela: par.valorParcela ?? 0,
            }))
          : [],
    }));
  } else if (isPrestador) {
    defaultValues.pagamentos = [
      {
        nomeBeneficiario: "",
        documento: "",
        idEstrangeiroBeneficiario: "",
        valorTotalContrato: mdfe?.valorTotalCarga ?? 0,
        formaPagamento: EFormaPagamentoMDFe.AVista,
        documentoResponsavelPagamento: "",
        idEstrangeiroResponsavelPagamento: "",
        cnpjInstituicaoPagamento: "",
        chavePixBancario: "",
        valorAdiantamento: undefined,
        tipoAntecipacao: undefined,
        codigoBanco: "",
        codigoAgencia: "",
        indicadorPagamentoBancario: undefined,
        componentes: [
          {
            tipoComponente: ETipoComponentePagamentoMDFe.Outros,
            valorComponente: mdfe?.valorTotalCarga ?? 0,
            descricaoOutro: "FRETE",
          },
        ],
        parcelas: [],
      },
    ];
  }

  const form = useForm<Required<PagamentoFormType>>({
    resolver: yupResolver(PagamentoSchema),
    defaultValues,
  });

  const pagamentosFieldArray = useFieldArray({
    control: form.control,
    name: "pagamentos",
  });

  const [modalidadesRecebimento, setModalidadesRecebimento] = useState<
    Record<string, "PIX" | "IPEF" | "BANCO">
  >({});

  const getModalidade = (
    fieldId: string,
    index: number
  ): "PIX" | "IPEF" | "BANCO" => {
    if (modalidadesRecebimento[fieldId]) {
      return modalidadesRecebimento[fieldId];
    }
    const currentIpef = form.getValues(
      `pagamentos.${index}.cnpjInstituicaoPagamento`
    );
    if (currentIpef && currentIpef.trim() !== "") return "IPEF";
    const currentBanco = form.getValues(`pagamentos.${index}.codigoBanco`);
    const currentAgencia = form.getValues(`pagamentos.${index}.codigoAgencia`);
    if (
      (currentBanco && currentBanco.trim() !== "") ||
      (currentAgencia && currentAgencia.trim() !== "")
    ) {
      return "BANCO";
    }
    return "PIX";
  };

  const handleSelectModalidade = (
    fieldId: string,
    index: number,
    modalidade: "PIX" | "IPEF" | "BANCO"
  ) => {
    setModalidadesRecebimento((prev) => ({ ...prev, [fieldId]: modalidade }));

    // Limpeza automática dos campos inativos para garantir exclusividade mútua exigida pela SEFAZ
    if (modalidade === "PIX") {
      form.setValue(`pagamentos.${index}.cnpjInstituicaoPagamento`, "");
      form.setValue(`pagamentos.${index}.codigoBanco`, "");
      form.setValue(`pagamentos.${index}.codigoAgencia`, "");
      form.setValue(`pagamentos.${index}.indicadorPagamentoBancario`, 1);
    } else if (modalidade === "IPEF") {
      form.setValue(`pagamentos.${index}.chavePixBancario`, "");
      form.setValue(`pagamentos.${index}.codigoBanco`, "");
      form.setValue(`pagamentos.${index}.codigoAgencia`, "");
      form.setValue(`pagamentos.${index}.indicadorPagamentoBancario`, undefined);
    } else if (modalidade === "BANCO") {
      form.setValue(`pagamentos.${index}.chavePixBancario`, "");
      form.setValue(`pagamentos.${index}.cnpjInstituicaoPagamento`, "");
    }

    form.clearErrors([
      `pagamentos.${index}.chavePixBancario`,
      `pagamentos.${index}.cnpjInstituicaoPagamento`,
      `pagamentos.${index}.codigoBanco`,
      `pagamentos.${index}.codigoAgencia`,
    ]);
  };

  const handleVoltar = () => {
    navigate("../totais");
  };

  const onSubmit = (data: PagamentoFormType) => {
    if (isPrestador && (!data.pagamentos || data.pagamentos.length === 0)) {
      showToast({
        message:
          "O grupo 'Informações de Pagamento do Frete' é obrigatório para emitente Prestador de Serviço de Transporte.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }

    // Validação da Rejeição 746: soma dos componentes deve ser igual ao valor do contrato
    if (data.pagamentos && data.pagamentos.length > 0) {
      for (let i = 0; i < data.pagamentos.length; i++) {
        const pag = data.pagamentos[i];
        const vContrato = Number(pag?.valorTotalContrato) || 0;
        const comps = pag?.componentes || [];
        if (comps.length > 0) {
          const somaComps = comps.reduce(
            (acc: number, c: any) => acc + (Number(c?.valorComponente) || 0),
            0
          );
          if (Math.abs(somaComps - vContrato) > 0.01) {
            const somaFmt = somaComps.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            const contratoFmt = vContrato.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
            showToast({
              message: `Pagamentos[${i}].ValorTotalContrato: A soma dos componentes do pagamento (${somaFmt}) deve ser igual ao valor do contrato (${contratoFmt}) (Rejeição 746).`,
              title: "Rejeição 746",
              variant: "Warning",
            });
            return;
          }
        }
      }
    }

    // Sanitizar e estruturar os Pagamentos
    const pagamentosTratados =
      data.pagamentos && data.pagamentos.length > 0
        ? data.pagamentos
            .map((p: any) => {
              const item: any = {
                valorTotalContrato: Number(p?.valorTotalContrato) || 0,
                formaPagamento: Number(p?.formaPagamento) === 1 ? 1 : 0,
                componentes: (p?.componentes || []).map((comp: any) => ({
                  tipoComponente: Number(comp?.tipoComponente),
                  valorComponente: Number(comp?.valorComponente) || 0,
                  descricaoOutro:
                    Number(comp?.tipoComponente) === 99
                      ? comp?.descricaoOutro?.trim() || "FRETE"
                      : undefined,
                })),
              };

              if (p?.nomeBeneficiario?.trim())
                item.nomeBeneficiario = p.nomeBeneficiario.trim();

              if (p?.documento?.trim()) {
                const doc = p.documento.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
                if (doc) {
                  item.documento = doc;
                  if (doc.length === 11) {
                    item.cpfBeneficiario = doc;
                    item.cpfResponsavelPagamento = doc;
                  } else if (doc.length === 14) {
                    item.cnpjBeneficiario = doc;
                    item.cnpjResponsavelPagamento = doc;
                  }
                }
              }

              if (p?.documentoResponsavelPagamento?.trim()) {
                const doc = p.documentoResponsavelPagamento
                  .replace(/[^A-Za-z0-9]/g, "")
                  .toUpperCase();
                if (doc) {
                  item.documentoResponsavelPagamento = doc;
                  if (doc.length === 11) item.cpfResponsavelPagamento = doc;
                  else if (doc.length === 14) item.cnpjResponsavelPagamento = doc;
                }
              }

              if (p?.idEstrangeiroBeneficiario?.trim()) {
                item.idEstrangeiroBeneficiario =
                  p.idEstrangeiroBeneficiario.trim();
              }

              if (p?.idEstrangeiroResponsavelPagamento?.trim()) {
                item.idEstrangeiroResponsavelPagamento =
                  p.idEstrangeiroResponsavelPagamento.trim();
              }
              if (p?.cnpjInstituicaoPagamento?.trim()) {
                const doc = p.cnpjInstituicaoPagamento
                  .replace(/[^A-Za-z0-9]/g, "")
                  .toUpperCase();
                if (doc) item.cnpjInstituicaoPagamento = doc;
              }
              if (p?.chavePixBancario?.trim() && !item.cnpjInstituicaoPagamento) {
                item.chavePixBancario = p.chavePixBancario.trim();
              }

              if (item.formaPagamento === 0) {
                item.valorAdiantamento = 0;
              } else if (p?.valorAdiantamento !== undefined && p?.valorAdiantamento !== null) {
                item.valorAdiantamento = Number(p.valorAdiantamento) || 0;
              }
              if (p?.tipoAntecipacao !== undefined && p?.tipoAntecipacao !== null) {
                item.tipoAntecipacao = Number(p.tipoAntecipacao) || 0;
              }
              if (p?.codigoBanco?.trim()) {
                item.codigoBanco = p.codigoBanco.replace(/\D/g, "").slice(0, 5);
              }
              if (p?.codigoAgencia?.trim()) {
                item.codigoAgencia = p.codigoAgencia.replace(/\D/g, "").slice(0, 5);
              }
              if (p?.indicadorPagamentoBancario !== undefined && p?.indicadorPagamentoBancario !== null) {
                item.indicadorPagamentoBancario = Number(p.indicadorPagamentoBancario) || undefined;
              }

              if (item.formaPagamento === 1 && p.parcelas && p.parcelas.length > 0) {
                item.parcelas = p.parcelas.map((par: any, idx: number) => ({
                  numeroParcela: Number(par?.numeroParcela) || idx + 1,
                  dataVencimento: moment(par?.dataVencimento, [
                    "DD/MM/YYYY",
                    "YYYY-MM-DD",
                    moment.ISO_8601,
                  ]).isValid()
                    ? moment(par?.dataVencimento, [
                        "DD/MM/YYYY",
                        "YYYY-MM-DD",
                        moment.ISO_8601,
                      ]).format("YYYY-MM-DD")
                    : String(par?.dataVencimento || ""),
                  valorParcela: Number(par?.valorParcela) || 0,
                }));
              }

              return item;
            })
            .filter((p) => p.valorTotalContrato > 0 && p.componentes.length > 0)
        : undefined;

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      pagamentos:
        pagamentosTratados && pagamentosTratados.length > 0
          ? pagamentosTratados
          : undefined,
    });

    navigate("../contratantes");
  };

  const onError = (errors: any) => {
    let specificMessage = "";
    if (errors.pagamentos) {
      if (Array.isArray(errors.pagamentos)) {
        for (let i = 0; i < errors.pagamentos.length; i++) {
          const pagErr = errors.pagamentos[i];
          if (!pagErr) continue;
          if (pagErr.valorTotalContrato?.message) {
            if (pagErr.valorTotalContrato.message.includes("Rejeição 746")) {
              specificMessage = `Pagamentos[${i}].ValorTotalContrato: ${pagErr.valorTotalContrato.message}`;
            } else {
              specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.valorTotalContrato.message}`;
            }
            break;
          }
          if (pagErr.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.message}`;
            break;
          }
          if (pagErr.documento?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.documento.message}`;
            break;
          }
          if (pagErr.cpfBeneficiario?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.cpfBeneficiario.message}`;
            break;
          }
          if (pagErr.cnpjBeneficiario?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.cnpjBeneficiario.message}`;
            break;
          }
          if (pagErr.chavePixBancario?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.chavePixBancario.message}`;
            break;
          }
          if (pagErr.cnpjInstituicaoPagamento?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.cnpjInstituicaoPagamento.message}`;
            break;
          }
          if (pagErr.codigoBanco?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.codigoBanco.message}`;
            break;
          }
          if (pagErr.codigoAgencia?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.codigoAgencia.message}`;
            break;
          }
          if (pagErr.parcelas?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.parcelas.message}`;
            break;
          }
          if (Array.isArray(pagErr.componentes)) {
            const hasCompInvalido = pagErr.componentes.some(
              (c: any) => c?.valorComponente?.message || c?.message
            );
            if (hasCompInvalido) {
              specificMessage = `Contrato de Pagamento #${i + 1}: Todos os componentes do frete devem possuir valor maior que zero.`;
              break;
            }
          } else if (pagErr.componentes?.message) {
            specificMessage = `Contrato de Pagamento #${i + 1}: ${pagErr.componentes.message}`;
            break;
          }
        }
      } else if (errors.pagamentos.message) {
        specificMessage = errors.pagamentos.message;
      }
    }

    if (!specificMessage) {
      const findFirstMessage = (obj: any): string | null => {
        if (!obj || typeof obj !== "object") return null;
        if (typeof obj.message === "string" && obj.message.trim() !== "") return obj.message;
        for (const k of Object.keys(obj)) {
          const m = findFirstMessage(obj[k]);
          if (m) return m;
        }
        return null;
      };
      const found = findFirstMessage(errors);
      if (found) specificMessage = found;
    }

    showToast({
      message:
        specificMessage ||
        "O grupo 'Informações de Pagamento do Frete' possui campos obrigatórios não preenchidos.",
      title: specificMessage?.includes("746")
        ? "Rejeição 746"
        : specificMessage?.includes("739") || specificMessage?.includes("740")
        ? "Rejeição SEFAZ"
        : "Campo Obrigatório",
      variant: "Warning",
    });
  };

  const handlePessoaSuccess = (value: PessoaDto) => {
    if (cadastroPagamentoIndex !== null) {
      form.setValue(
        `pagamentos.${cadastroPagamentoIndex}.nomeBeneficiario`,
        value.nome
      );
      form.setValue(
        `pagamentos.${cadastroPagamentoIndex}.documento`,
        formatarDoc(value.documento)
      );
    }
    setCadastroPagamentoIndex(null);
  };

  const handlePessoaCancel = () => {
    setCadastroPagamentoIndex(null);
  };

  if (cadastroPagamentoIndex !== null) {
    return (
      <Row className="mt-3">
        <PessoaCadastroView
          onSuccess={handlePessoaSuccess}
          onCancel={handlePessoaCancel}
        />
      </Row>
    );
  }

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      {/* Dados de Pagamento do Frete */}
      <div className="card shadow-sm p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="text-primary mb-0">
              Informações de Pagamento do Frete
              {isPrestador && (
                <Badge bg="danger" className="ms-2">
                  Obrigatório
                </Badge>
              )}
            </h5>
            <small className="text-muted">
              Regulamentação ANTT Res. 5.862 e composição dos custos do frete
            </small>
          </div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              pagamentosFieldArray.append({
                nomeBeneficiario: "",
                documento: "",
                idEstrangeiroBeneficiario: "",
                valorTotalContrato: undefined as any,
                formaPagamento: EFormaPagamentoMDFe.AVista,
                documentoResponsavelPagamento: "",
                idEstrangeiroResponsavelPagamento: "",
                cnpjInstituicaoPagamento: "",
                chavePixBancario: "",
                valorAdiantamento: undefined,
                tipoAntecipacao: undefined,
                codigoBanco: "",
                codigoAgencia: "",
                indicadorPagamentoBancario: undefined,
                componentes: [
                  {
                    tipoComponente: ETipoComponentePagamentoMDFe.Frete,
                    valorComponente: undefined as any,
                    descricaoOutro: "",
                  },
                ],
                parcelas: [],
              })
            }
          >
            + Adicionar Pagamento
          </Button>
        </div>

        {pagamentosFieldArray.fields.length === 0 ? (
          <div className="text-muted small py-2">
            Nenhum dado de pagamento configurado (opcional para carga própria).
          </div>
        ) : (
          pagamentosFieldArray.fields.map((field, index) => {
            const formaPag = form.watch(`pagamentos.${index}.formaPagamento`);
            const pagErrors = form.formState.errors.pagamentos?.[index];
            const modalidadeAtiva = getModalidade(field.id, index);
            const comps = form.watch(`pagamentos.${index}.componentes`) || [];
            const somaComps = comps.reduce(
              (acc: number, c: any) => acc + (Number(c?.valorComponente) || 0),
              0
            );
            const vContrato =
              Number(form.watch(`pagamentos.${index}.valorTotalContrato`)) || 0;
            const diferenca = Number((somaComps - vContrato).toFixed(2));
            const temDivergencia = vContrato > 0 && Math.abs(diferenca) > 0.01;
            const ehIgual = vContrato > 0 && Math.abs(diferenca) <= 0.01;

            return (
              <div key={field.id} className="mb-4 p-3 border rounded shadow-sm">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="fw-semibold text-secondary">
                    Contrato de Pagamento #{index + 1}
                  </span>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      pagamentosFieldArray.remove(index);
                      setModalidadesRecebimento((prev) => {
                        const novo = { ...prev };
                        delete novo[field.id];
                        return novo;
                      });
                    }}
                  >
                    Remover Pagamento
                  </Button>
                </div>

                {pagErrors?.message && (
                  <div className="alert alert-danger py-2 px-3 small mb-3">
                    <i className="bi bi-exclamation-triangle-fill me-2" />
                    {pagErrors.message}
                  </div>
                )}

                {/* Sub-grupo 1: Dados Gerais do Contrato */}
                <h6 className="text-secondary small fw-bold mt-2 mb-2">
                  Dados do Contrato
                </h6>
                <Row className="g-3 mb-3">
                  <Col xs={12} md={4}>
                    <FloatSelect
                      label="Forma de Pagamento *"
                      options={formaPagamentoMDFeOptions}
                      register={form.register(
                        `pagamentos.${index}.formaPagamento`,
                        {
                          valueAsNumber: true,
                          onChange: (e) => {
                            if (Number(e.target.value) === 0) {
                              form.setValue(
                                `pagamentos.${index}.valorAdiantamento`,
                                undefined as any
                              );
                            }
                          },
                        }
                      )}
                      errorsMessage={pagErrors?.formaPagamento}
                    />
                  </Col>

                  <Col xs={12} md={4}>
                    <FloatNumericInput
                      label="Valor Total do Contrato (R$) *"
                      name={`pagamentos.${index}.valorTotalContrato`}
                      control={form.control}
                      decimalScale={2}
                      errorsMessage={pagErrors?.valorTotalContrato}
                    />
                  </Col>

                  <Col xs={12} md={2}>
                    <FloatNumericInput
                      label="Valor Adiantamento (R$)"
                      name={`pagamentos.${index}.valorAdiantamento`}
                      control={form.control}
                      decimalScale={2}
                      disabled={Number(formaPag) === 0}
                      tooltipMessage={
                        Number(formaPag) === 0
                          ? "Não permitido para pagamento à vista (Rejeição 739)"
                          : ""
                      }
                      errorsMessage={pagErrors?.valorAdiantamento}
                    />
                  </Col>

                  <Col xs={12} md={2}>
                    <FloatInput
                      label="Tipo Antecipação (0-9)"
                      inputMask="int"
                      maxLength={1}
                      registerField={form.register(
                        `pagamentos.${index}.tipoAntecipacao`,
                        { valueAsNumber: true }
                      )}
                      errorsMessage={pagErrors?.tipoAntecipacao}
                    />
                  </Col>
                </Row>

                {/* Sub-grupo 2: Identificação do Beneficiário / Responsável */}
                <div className="d-flex align-items-center justify-content-between mt-3 mb-1">
                  <h6 className="text-secondary small fw-bold mb-0">
                    Identificação do Beneficiário / Responsável <span className="text-danger">*</span>
                  </h6>
                  <small className="text-muted">
                    Informe CPF ou CNPJ do beneficiário
                  </small>
                </div>
                <Row className="g-3 mb-3 align-items-start">
                  <Col xs={12} md={6}>
                    <DropdownFilter<PessoaDto>
                      fetchData={pessoaBuscar.PessoaBuscar}
                      loading={pessoaBuscar.loading}
                      labelDescription="Nome do Beneficiário"
                      getDescription={(item: PessoaDto) => item.nome}
                      getId={(item: PessoaDto) => item.id}
                      registeredFields={{
                        id: form.register(
                          `pagamentos.${index}.nomeBeneficiario`
                        ),
                        description: form.register(
                          `pagamentos.${index}.nomeBeneficiario`
                        ),
                      }}
                      errorsMessageDescription={pagErrors?.nomeBeneficiario}
                      onSelect={(item: PessoaDto | undefined) => {
                        if (item) {
                          form.setValue(
                            `pagamentos.${index}.nomeBeneficiario`,
                            item.nome
                          );
                          form.setValue(
                            `pagamentos.${index}.documento`,
                            formatarDoc(item.documento)
                          );
                        }
                      }}
                    />
                    <div className="mt-1">
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none text-muted"
                        onClick={() => setCadastroPagamentoIndex(index)}
                      >
                        + Cadastrar
                      </Button>
                    </div>
                  </Col>

                  <Col xs={12} md={6}>
                    <FloatInput
                      label="CPF / CNPJ do Beneficiário"
                      inputMask="cnpj-cpf"
                      maxLength={18}
                      registerField={form.register(
                        `pagamentos.${index}.documento`
                      )}
                      errorsMessage={
                        pagErrors?.documento ||
                        pagErrors?.cnpjBeneficiario ||
                        pagErrors?.cpfBeneficiario
                      }
                    />
                  </Col>
                </Row>

                {/* Sub-grupo 3: Dados de Recebimento do Frete (infBanc) */}
                <div className="mt-3 p-3 bg-light rounded border">
                  <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                    <div>
                      <h6 className="text-secondary small fw-bold mb-0">
                        Dados de Recebimento do Frete (infBanc) <span className="text-danger">*</span>
                      </h6>
                      <small className="text-muted">
                        Selecione a modalidade e informe os dados correspondentes
                      </small>
                    </div>

                    {/* Pills de seleção rápida em 1 clique */}
                    <div
                      className="btn-group"
                      role="group"
                      aria-label="Modalidade de recebimento do frete"
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          modalidadeAtiva === "PIX"
                            ? "primary"
                            : "outline-secondary"
                        }
                        onClick={() =>
                          handleSelectModalidade(field.id, index, "PIX")
                        }
                        className="d-flex align-items-center fw-semibold"
                      >
                        <FontAwesomeIcon icon={faQrcode} className="me-1" />
                        Chave PIX
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          modalidadeAtiva === "IPEF"
                            ? "primary"
                            : "outline-secondary"
                        }
                        onClick={() =>
                          handleSelectModalidade(field.id, index, "IPEF")
                        }
                        className="d-flex align-items-center fw-semibold"
                      >
                        <FontAwesomeIcon icon={faBuilding} className="me-1" />
                        CNPJ IPEF
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={
                          modalidadeAtiva === "BANCO"
                            ? "primary"
                            : "outline-secondary"
                        }
                        onClick={() =>
                          handleSelectModalidade(field.id, index, "BANCO")
                        }
                        className="d-flex align-items-center fw-semibold"
                      >
                        <FontAwesomeIcon icon={faUniversity} className="me-1" />
                        Conta Bancária
                      </Button>
                    </div>
                  </div>

                  {/* Renderização condicional e dinâmica dos campos conforme a modalidade ativa */}
                  {modalidadeAtiva === "PIX" && (
                    <Row className="g-3 mt-1">
                      <Col xs={12} md={8}>
                        <FloatInput
                          label="Chave PIX *"
                          maxLength={60}
                          registerField={form.register(
                            `pagamentos.${index}.chavePixBancario`
                          )}
                          errorsMessage={pagErrors?.chavePixBancario}
                        />
                        <div className="form-text text-muted small mt-1">
                          Informe a Chave PIX (CPF, CNPJ, e-mail, telefone ou chave aleatória).
                        </div>
                      </Col>
                    </Row>
                  )}

                  {modalidadeAtiva === "IPEF" && (
                    <Row className="g-3 mt-1">
                      <Col xs={12} md={6}>
                        <FloatInput
                          label="CNPJ da IPEF *"
                          inputMask="cnpj"
                          maxLength={18}
                          registerField={form.register(
                            `pagamentos.${index}.cnpjInstituicaoPagamento`
                          )}
                          errorsMessage={
                            pagErrors?.cnpjInstituicaoPagamento ||
                            pagErrors?.chavePixBancario
                          }
                        />
                        <div className="form-text text-muted small mt-1">
                          CNPJ da Instituição de Pagamento Eletrônico de Frete habilitada na ANTT.
                        </div>
                      </Col>
                    </Row>
                  )}

                  {modalidadeAtiva === "BANCO" && (
                    <Row className="g-3 mt-1">
                      <Col xs={12} md={6}>
                        <BancoCompeInputComponent
                          registerField={form.register(
                            `pagamentos.${index}.codigoBanco`
                          )}
                          value={
                            form.watch(`pagamentos.${index}.codigoBanco`) ?? ""
                          }
                          onChangeValue={(codigo) => {
                            form.setValue(
                              `pagamentos.${index}.codigoBanco`,
                              codigo,
                              { shouldValidate: true, shouldDirty: true }
                            );
                          }}
                          errorsMessage={
                            pagErrors?.codigoBanco ||
                            pagErrors?.chavePixBancario
                          }
                        />
                      </Col>

                      <Col xs={12} md={6}>
                        <FloatInput
                          label="Código da Agência *"
                          inputMask="int"
                          maxLength={10}
                          registerField={form.register(
                            `pagamentos.${index}.codigoAgencia`
                          )}
                          errorsMessage={
                            pagErrors?.codigoAgencia || pagErrors?.chavePixBancario
                          }
                        />
                        <div className="form-text text-muted small mt-1">
                          Número da agência bancária (1 a 10 dígitos).
                        </div>
                      </Col>
                    </Row>
                  )}
                </div>

                {/* Componentes do Frete */}
                <div className="mt-3 p-3 bg-light rounded border">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="small fw-semibold text-secondary">
                      Composição do Frete (Componentes) <span className="text-danger">*</span>
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        const currentComps =
                          form.getValues(`pagamentos.${index}.componentes`) || [];
                        form.setValue(`pagamentos.${index}.componentes`, [
                          ...currentComps,
                          {
                            tipoComponente:
                              ETipoComponentePagamentoMDFe.Frete,
                            valorComponente: 0,
                            descricaoOutro: "",
                          },
                        ]);
                      }}
                    >
                      + Componente
                    </Button>
                  </div>

                  {(form.watch(`pagamentos.${index}.componentes`) || []).map(
                    (comp, compIndex) => (
                      <Row key={compIndex} className="g-2 mb-2 align-items-start">
                        <Col xs={12} md={4}>
                          <FloatSelect
                            label="Tipo de Componente"
                            options={tipoComponentePagamentoMDFeOptions}
                            register={form.register(
                              `pagamentos.${index}.componentes.${compIndex}.tipoComponente`,
                              { valueAsNumber: true }
                            )}
                          />
                        </Col>
                        <Col xs={12} md={4}>
                          <FloatNumericInput
                            label="Valor (R$)"
                            name={`pagamentos.${index}.componentes.${compIndex}.valorComponente`}
                            control={form.control}
                            decimalScale={2}
                            errorsMessage={
                              (pagErrors?.componentes as any)?.[compIndex]
                                ?.valorComponente
                            }
                          />
                        </Col>
                        <Col xs={10} md={3}>
                          <FloatInput
                            label="Descrição (se Outros)"
                            registerField={form.register(
                              `pagamentos.${index}.componentes.${compIndex}.descricaoOutro`
                            )}
                            maxLength={60}
                            disabled={
                              Number(
                                form.watch(
                                  `pagamentos.${index}.componentes.${compIndex}.tipoComponente`
                                )
                              ) !== 99
                            }
                            errorsMessage={
                              (pagErrors?.componentes as any)?.[compIndex]
                                ?.descricaoOutro
                            }
                          />
                        </Col>
                        <Col xs={2} md={1} className="text-end pt-2">
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              const current =
                                form.getValues(
                                  `pagamentos.${index}.componentes`
                                ) || [];
                              if (current.length > 1) {
                                form.setValue(
                                  `pagamentos.${index}.componentes`,
                                  current.filter((_, i) => i !== compIndex)
                                );
                              }
                            }}
                          >
                            ×
                          </Button>
                        </Col>
                      </Row>
                    )
                  )}

                  {/* Totalizador da Composição do Frete e Validação Rejeição 746 */}
                  <div className="d-flex flex-wrap justify-content-between align-items-center pt-2 mt-2 border-top small">
                    <div className="text-body-secondary">
                      Total dos Componentes:{" "}
                      <strong className={temDivergencia ? "text-danger" : "text-body-emphasis"}>
                        {somaComps.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })}
                      </strong>
                      {vContrato > 0 && (
                        <span className="ms-2 text-body-secondary">
                          (Contrato: {vContrato.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })})
                        </span>
                      )}
                    </div>

                    {temDivergencia && (
                      <div className="text-danger fw-semibold">
                        <i className="bi bi-exclamation-triangle-fill me-1" />
                        Diferença: {diferenca > 0 ? "+" : ""}{diferenca.toLocaleString("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        })} (Rejeição 746)
                      </div>
                    )}

                    {ehIgual && (
                      <div className="text-success fw-semibold">
                        <i className="bi bi-check-circle-fill me-1" />
                        Soma igual ao contrato
                      </div>
                    )}
                  </div>
                </div>

                {/* Parcelas se formaPagamento == 1 (A Prazo) */}
                {Number(formaPag) === 1 && (
                  <div className="mt-3 p-3 bg-light rounded border">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="small fw-semibold text-secondary">
                        Cronograma de Parcelas (A Prazo) <span className="text-danger">*</span>
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => {
                          const currentParcs =
                            form.getValues(`pagamentos.${index}.parcelas`) || [];
                          const total =
                            Number(
                              form.getValues(
                                `pagamentos.${index}.valorTotalContrato`
                              )
                            ) || 0;
                          const valores = distribuirValorParcelas(
                            total,
                            currentParcs.length + 1
                          );
                          form.setValue(`pagamentos.${index}.parcelas`, [
                            ...currentParcs,
                            {
                              numeroParcela: currentParcs.length + 1,
                              dataVencimento: moment()
                                .add(currentParcs.length + 1, "month")
                                .format("YYYY-MM-DD"),
                              valorParcela: 0,
                            },
                          ].map((parc, i) => ({
                            ...parc,
                            valorParcela: valores[i],
                          })));
                        }}
                      >
                        + Parcela
                      </Button>
                    </div>

                    {(form.watch(`pagamentos.${index}.parcelas`) || []).map(
                      (parc, parcIndex) => (
                        <Row key={parcIndex} className="g-2 mb-2 align-items-start">
                          <Col xs={3} md={2}>
                            <FloatInput
                              label="Nº Parcela"
                              inputMask="int"
                              maxLength={3}
                              registerField={form.register(
                                `pagamentos.${index}.parcelas.${parcIndex}.numeroParcela`,
                                { valueAsNumber: true }
                              )}
                              errorsMessage={
                                (pagErrors?.parcelas as any)?.[parcIndex]
                                  ?.numeroParcela
                              }
                            />
                          </Col>
                          <Col xs={4} md={4}>
                            <FloatInput
                              label="Vencimento (DD/MM/AAAA) *"
                              inputMask="data"
                              maxLength={10}
                              registerField={form.register(
                                `pagamentos.${index}.parcelas.${parcIndex}.dataVencimento`
                              )}
                              errorsMessage={
                                (pagErrors?.parcelas as any)?.[parcIndex]
                                  ?.dataVencimento
                              }
                            />
                          </Col>
                          <Col xs={3} md={5}>
                            <FloatNumericInput
                              label="Valor da Parcela (R$)"
                              name={`pagamentos.${index}.parcelas.${parcIndex}.valorParcela`}
                              control={form.control}
                              decimalScale={2}
                              errorsMessage={
                                (pagErrors?.parcelas as any)?.[parcIndex]
                                  ?.valorParcela
                              }
                            />
                          </Col>
                          <Col xs={2} md={1} className="text-end pt-2">
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                const current =
                                  form.getValues(
                                    `pagamentos.${index}.parcelas`
                                  ) || [];
                                form.setValue(
                                  `pagamentos.${index}.parcelas`,
                                  current
                                    .filter((_, i) => i !== parcIndex)
                                    .map((parc, i) => ({
                                      ...parc,
                                      numeroParcela: i + 1,
                                    }))
                                );
                              }}
                            >
                              ×
                            </Button>
                          </Col>
                        </Row>
                      )
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Botões de navegação no rodapé */}
      <WizardFooter onVoltar={handleVoltar} />
    </Form>
  );
}

export default MDFeCadastroPagamentoView;
